import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { RouteMeta } from '../utils/usePageMeta';
import {
  BUILDER_ABOUT_US_HIDDEN_BLOCKS,
  BUILDER_CONTENT_IDS,
  BUILDER_FIGMA_MODEL,
  BUILDER_INVESTOR_FAQ_URL,
  BUILDER_INVESTOR_PAGE_URL,
  BUILDER_STARTUP_PAGE_URL,
} from '../config/builder';
import Header from '../components/Header';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Footer from '../components/Footer';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminRoute from '../components/AdminRoute';
import LegacyRedirect from '../utils/LegacyRedirect';
import LoggedInInvestorHome from '../components/LoggedInInvestorHome';
import CloudFunctionsTest from '../components/CloudFunctionsTest';
import BuilderTest from '../components/builder/BuilderTest';
import BuilderPage from '../components/builder/BuilderPage';
import SignupPage from '../pages/SignupPage';
import LoginPage from '../pages/LoginPage';
import DashboardPage from '../pages/DashboardPage';
import SettingsPage from '../pages/SettingsPage';
import NotificationsSettingsPage from '../pages/NotificationsSettingsPage';
import LandingStartupPage from '../pages/LandingStartupPage';
import LandingInvestorPage from '../pages/LandingInvestorPage';
import PasswordResetRequestPage from '../pages/PasswordResetRequestPage';
import UnsubscribePage from '../pages/UnsubscribePage';
import SignupContinuePage from '../pages/SignupContinuePage';
import FinalStepPage from '../pages/FinalStepPage';
import StartupProfilePage from '../pages/StartupProfilePage';
import InvestorProfilePage from '../pages/InvestorProfilePage';
import MyProfilePage from '../pages/MyProfilePage';
import ExpressSignUpPage from '../pages/ExpressSignUpPage';
import ContactPage from '../pages/ContactPage';
import CheckoutSuccess from '../pages/CheckoutSuccess';
import CheckoutCancel from '../pages/CheckoutCancel';
import UsersPage from '../pages/UsersPage';
import ApproveUserPage from '../pages/ApproveUserPage';
import InvitationsPage from '../pages/InvitationsPage';
import AdminDashboardPage from '../pages/AdminDashboardPage';
import AdminPartnersPage from '../pages/AdminPartnersPage';
import AdminPromoCodesPage from '../pages/AdminPromoCodesPage';
import AdminDealsPage from '../pages/AdminDealsPage';
import AdminLisaPreferencesPage from '../pages/AdminLisaPreferencesPage';
import StartupProfileViewPage from '../pages/StartupProfileViewPage';
import InvestorProfileViewPage from '../pages/InvestorProfileViewPage';
import PauseProfilePage from '../pages/PauseProfilePage';
import RemoveProfilePage from '../pages/RemoveProfilePage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import EmailActionPage from '../pages/EmailActionPage';
import NotFoundPage from '../pages/NotFoundPage';
import TermsPage from '../pages/TermsPage';
import PrivacyPage from '../pages/PrivacyPage';
import CookiesPage from '../pages/CookiesPage';
import FavoritesPage from '../pages/FavoritesPage';
import InboxPage from '../pages/InboxPage';
import SharedStartupPage from '../pages/SharedStartupPage';

// Old v1 partner referral links (e.g. /invitedpartner?utm_source=...&utm_campaign=CODE)
// point at a page that no longer exists in v2. Redirect to signup, keeping the
// query string so SignUp.tsx can still auto-apply the promo code from utm_campaign.
const InvitedPartnerRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/startups/pricing${location.search}`} replace />;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const isVerifyEmailPage =
    location.pathname === '/verify-email' || location.pathname === '/email-action';

  return (
    <div className="flex flex-1 flex-col">
      <RouteMeta />
      {!isVerifyEmailPage && <AnnouncementBanner />}
      {!isVerifyEmailPage && <Header />}

      <div className="flex-1">
        <Routes>
          <Route
            path="/"
            element={
              <LoggedInInvestorHome>
                <BuilderPage urlPath="/prairi" model="figma-imports" />
              </LoggedInInvestorHome>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/password-reset" element={<PasswordResetRequestPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/continue" element={<SignupContinuePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/email-action" element={<EmailActionPage />} />
          <Route path="/finalstep/:userId" element={<FinalStepPage />} />
          <Route
            path="/dashboard/:startupSlug?"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites/:startupSlug"
            element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <InboxPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <NotificationsSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/landing/startup"
            element={
              <ProtectedRoute>
                <LandingStartupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/landing/investor"
            element={
              <ProtectedRoute>
                <LandingInvestorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/startup/view"
            element={
              <ProtectedRoute>
                <StartupProfileViewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/startup"
            element={
              <ProtectedRoute>
                <StartupProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/investor/view"
            element={
              <ProtectedRoute>
                <InvestorProfileViewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/investor/:step?"
            element={
              <ProtectedRoute>
                <InvestorProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/startup/:step"
            element={
              <ProtectedRoute>
                <StartupProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/myprofile"
            element={
              <ProtectedRoute>
                <MyProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="/express-signup" element={<ExpressSignUpPage />} />
          <Route path="/test" element={<CloudFunctionsTest />} />
          <Route path="/builder-test" element={<BuilderTest />} />

          {/* ── Public SEO routes ── */}
          <Route
            path="/investors"
            element={
              <LoggedInInvestorHome>
                <BuilderPage urlPath={BUILDER_INVESTOR_PAGE_URL} model={BUILDER_FIGMA_MODEL} />
              </LoggedInInvestorHome>
            }
          />
          <Route
            path="/investors/pricing"
            element={<SignupPage key="investor-signup" userType="investor" />}
          />
          <Route
            path="/investors/faq"
            element={<BuilderPage urlPath={BUILDER_INVESTOR_FAQ_URL} model="page" />}
          />
          <Route
            path="/startups"
            element={<BuilderPage urlPath={BUILDER_STARTUP_PAGE_URL} model={BUILDER_FIGMA_MODEL} />}
          />
          <Route path="/startup" element={<Navigate to="/startups" replace />} />
          <Route
            path="/startups/pricing"
            element={<SignupPage key="startup-signup" userType="startup" />}
          />
          <Route
            path="/startups/faq"
            element={<BuilderPage urlPath="/startups-faq" model="page" />}
          />
          <Route
            path="/about-us"
            element={
              <BuilderPage
                contentId={BUILDER_CONTENT_IDS.aboutUs}
                model="page"
                hiddenBlockIds={BUILDER_ABOUT_US_HIDDEN_BLOCKS}
              />
            }
          />
          <Route path="/about" element={<Navigate to="/about-us" replace />} />

          {/* ── Client-side redirects for old URLs (Firebase handles 301 for external links) ── */}
          <Route path="/home" element={<LegacyRedirect to="/" />} />
          <Route path="/discover" element={<LegacyRedirect to="/investors" />} />
          <Route path="/in" element={<LegacyRedirect to="/investors" />} />
          <Route path="/invitation/investor" element={<LegacyRedirect to="/investors/pricing" />} />
          <Route path="/invitation/startup" element={<LegacyRedirect to="/startups/pricing" />} />
          <Route path="/signup/investor" element={<LegacyRedirect to="/investors/pricing" />} />
          <Route path="/signup/startup" element={<LegacyRedirect to="/startups/pricing" />} />
          <Route path="/sub1" element={<LegacyRedirect to="/startups/pricing" />} />
          <Route path="/sub2" element={<LegacyRedirect to="/investors/pricing" />} />
          <Route path="/getfunded" element={<LegacyRedirect to="/startups" />} />
          <Route path="/getfunded/:docId" element={<LegacyRedirect to="/startups" />} />
          <Route path="/invitedpartner" element={<InvitedPartnerRedirect />} />
          <Route path="/investor-faq" element={<LegacyRedirect to="/investors/faq" />} />
          <Route path="/startup-faq" element={<LegacyRedirect to="/startups/faq" />} />
          <Route path="/startups-faq" element={<LegacyRedirect to="/startups/faq" />} />

          <Route path="/shared/:shareId" element={<SharedStartupPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <UsersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admindashboard"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />
          <Route
            path="/stats"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />
          <Route
            path="/approve/:id"
            element={
              <AdminRoute>
                <ApproveUserPage />
              </AdminRoute>
            }
          />
          <Route
            path="/invitations"
            element={
              <AdminRoute>
                <InvitationsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/partners"
            element={
              <AdminRoute>
                <AdminPartnersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/promocodes"
            element={
              <AdminRoute>
                <AdminPromoCodesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/deals"
            element={
              <AdminRoute>
                <AdminDealsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/lisa"
            element={
              <AdminRoute>
                <AdminLisaPreferencesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/pause-profile"
            element={
              <ProtectedRoute>
                <PauseProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/remove"
            element={
              <ProtectedRoute>
                <RemoveProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          {/* Catch-all route for 404 - must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
};

export default AppRoutes;
